import { useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { DashboardLayout, type NavItem } from '@vnetwork/ui/shells';
import { useAppShellOptional } from '@vnetwork/ui/layout';
import { NAV, NAV_LEAVES, type WorkspaceNavItem } from './nav';
import { TopbarActions } from './topbar-actions';
import { WorkspaceSidebarNav } from './sidebar-nav';
import { CommandMenu } from './CommandMenu';
import styles from './app-layout.module.css';

/** Resolve i18n labels recursively across nav groups + children. */
function resolveNav(items: WorkspaceNavItem[], t: (k: string) => string): NavItem[] {
  return items.map((item) => ({
    ...item,
    label: t(item.labelKey),
    items: item.items ? resolveNav(item.items, t) : undefined,
  }));
}

/**
 * Authenticated application frame — the standard DashboardLayout shell driven by
 * the datacentral-derived NAV config. Active item is derived from the current
 * path (longest-prefix over the flattened leaves); clicking a nav item routes
 * via react-router (external items open in a new tab). ⌘K opens the CommandMenu.
 */
export function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [cmdOpen, setCmdOpen] = useState(false);

  const nav = useMemo(() => resolveNav(NAV, t), [t]);

  // Longest-prefix match over leaves so nested routes keep the parent active.
  // '/' only matches exactly.
  const activeId = useMemo(() => {
    const match = [...NAV_LEAVES]
      .filter((n) =>
        n.path === '/' ? location.pathname === '/' : location.pathname.startsWith(n.path!),
      )
      .sort((a, b) => (b.path!.length ?? 0) - (a.path!.length ?? 0))[0];
    return match?.id ?? 'dashboard';
  }, [location.pathname]);

  const onNavigate = (item: NavItem) => {
    const node = item as WorkspaceNavItem;
    if (!node.path) return; // pure group — toggles expand, no route
    if (node.external) {
      window.open(node.path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigate(node.path);
  };

  return (
    <>
      <DashboardLayout
        brand={<Brand />}
        nav={<WorkspaceSidebarNav items={nav} activeId={activeId} onNavigate={onNavigate} />}
        search={<SearchTrigger onClick={() => setCmdOpen(true)} />}
        topbarActions={<TopbarActions />}
        containerSize="full"
        flush
      >
        <Outlet />
      </DashboardLayout>
      <CommandMenu open={cmdOpen} onOpenChange={setCmdOpen} />
    </>
  );
}

/** Topbar/sidebar brand — logo + "Workspace" wordmark. When the sidebar is
 * collapsed the wordmark is hidden and the logo stays centered (never blank). */
function Brand() {
  const shell = useAppShellOptional();
  const collapsed = shell?.collapsed ?? false;
  return (
    <Link
      to="/"
      aria-label="Workspace"
      className={`${styles.brand} ${collapsed ? styles['brand-collapsed'] : ''}`}
    >
      <img src="/assets/img/icon.png" alt="" width={22} height={22} style={{ objectFit: 'contain', flexShrink: 0 }} />
      {collapsed ? null : 'Workspace'}
    </Link>
  );
}

/** Sidebar search affordance — a button styled like an input that opens ⌘K. */
function SearchTrigger({ onClick }: { onClick: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      className={styles['search-trigger']}
    >
      <Search size={15} />
      <span style={{ flex: 1, textAlign: 'left' }}>{t('common.search')}</span>
      <kbd className={styles['search-kbd']}>⌘K</kbd>
    </button>
  );
}
